import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions } from "../spec";

const SHEET = "AV Guardias";

// Weekly on-call grid with gaps (shifts nobody covered yet).
const GUARDIAS: [string, string, string, string][] = [
  ["Lunes", "Carlos", "Lucía", "Andrés"],
  ["Martes", "Valeria", "", "Carlos"],
  ["Miércoles", "Lucía", "Miguel", ""],
  ["Jueves", "Andrés", "Carlos", "Valeria"],
  ["Viernes", "", "Miguel", "Lucía"],
  ["Sábado", "Isabel", "", "Andrés"],
  ["Domingo", "Carlos", "Isabel", ""],
];
const INVITADOS = [
  "Ana", "Luis", "Marta", "Jorge", "Sofía", "Diego", "Paola", "Ricardo",
  "Daniela", "Fernando", "Gabriela", "Javier", "Camila", "Raúl",
];

const turnos = GUARDIAS.map((g) => g.slice(1));
const porFila = turnos.flat().filter((n) => n !== "");
const porColumna = [0, 1, 2].flatMap((c) => turnos.map((t) => t[c])).filter((n) => n !== "");
const byText = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });
const personas = [...new Set(porFila)].sort(byText);
const wrapRows = (list: string[], n: number): CellValue[][] =>
  Array.from({ length: Math.ceil(list.length / n) }, (_, r) =>
    Array.from({ length: n }, (_, c) => list[r * n + c] ?? "")
  );
const wrapCols = (list: string[], n: number): CellValue[][] => {
  const cols = Math.ceil(list.length / n);
  return Array.from({ length: n }, (_, r) => Array.from({ length: cols }, (_, c) => list[c * n + r] ?? ""));
};

const col = (values: CellValue[]) => values.map((v) => [v]);
const EXPECTED: CellValue[][][] = [
  col(porFila),
  col(porColumna),
  col(personas),
  wrapRows(INVITADOS, 3),
  wrapCols(INVITADOS, 5),
  personas.map((p) => [p, porFila.filter((n) => n === p).length]),
];
const FNS = [["TOCOL"], ["TOCOL"], ["SORT", "UNIQUE", "TOCOL"], ["WRAPROWS"], ["WRAPCOLS"], ["LET", "HSTACK", "COUNTIF", "TOCOL"]];
const LABELS = [
  "Tarea 1 · Lista de guardias (por fila)",
  "Tarea 2 · Lista de guardias (por columna)",
  "Tarea 3 · Personas en guardia",
  "Tarea 4 · Invitados en 3 columnas",
  "Tarea 5 · Invitados en columnas de 5",
  "Tarea 6 · Guardias por persona",
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`J${row}`);
  return row + result.length + 3;
}, 5);

const RANGO = "Guardias[[Turno 1]:[Turno 3]]";
const LET_FORMULA = `=LET(p,ORDENAR(UNICOS(ENCOL(${RANGO},1))),APILARH(p,CONTAR.SI(${RANGO},p)))`;

export const spec: FuncionProjectSpec = {
  projectId: "funciones-encol",
  sheets: [
    {
      name: SHEET,
      title: "Reacomodar datos",
      subtitle: "Rol de guardias y lista de invitados — cambia la forma de los datos sin copiar celda por celda",
      tables: [
        { name: "Guardias", anchor: "A4", headers: ["Día", "Turno 1", "Turno 2", "Turno 3"], rows: GUARDIAS },
        { name: "Invitados", anchor: "F4", headers: ["Nombre"], rows: INVITADOS.map((n) => [n]) },
      ],
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected, exact: true },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-encol",
  title: "ENCOL y AJUSTARFILAS: Reacomodar datos",
  subtitle: "De cuadrícula a lista y de lista a cuadrícula",
  description:
    "Tienes el rol de guardias de la semana en una cuadrícula (días × turnos) con algunos huecos, y una lista larga de invitados que hay que acomodar en columnas para imprimir gafetes.\n\nENCOL convierte cualquier rango en una sola columna (ENFILA, en una fila). AJUSTARFILAS y AJUSTARCOLS hacen lo contrario: reparten una lista en una cuadrícula del ancho o alto que elijas.",
  topics: [
    "ENCOL ignorando celdas vacías",
    "Recorrer por filas o por columnas",
    "Lista única de personas: ORDENAR(UNICOS(ENCOL(...)))",
    "AJUSTARFILAS y AJUSTARCOLS con relleno",
    "LET + APILARH: contar por persona",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Guardias en una lista",
      description: `En la hoja <strong>AV Guardias</strong>, celda <strong>${ANCHORS[0]}</strong>: convierte los turnos de la tabla <strong>Guardias</strong> (columnas Turno 1 a Turno 3) en <strong>una sola columna</strong>, <strong>sin</strong> las celdas vacías.`,
      solution: [
        `Selecciona ${F(ANCHORS[0])}.`,
        `El segundo argumento de ENCOL, ignorar, con ${F("1")} se salta las celdas en blanco. Sin él aparecerían ceros.`,
        `Escribe ${F(`=ENCOL(${RANGO},1)`)}.`,
      ],
      expectedResult: "Lunes: Carlos, Lucía, Andrés; martes: Valeria, Carlos…",
      finalFormula: `=ENCOL(${RANGO},1)`,
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Recorrer por columnas",
      description: `En <strong>${ANCHORS[1]}</strong>: la misma lista, pero recorriendo la cuadrícula <strong>por columnas</strong>: primero todo el Turno 1, luego el Turno 2 y luego el Turno 3.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        `El tercer argumento, por_columna, con ${F("VERDADERO")} lee de arriba abajo antes de pasar a la siguiente columna.`,
        `Escribe ${F(`=ENCOL(${RANGO},1,VERDADERO)`)}.`,
      ],
      expectedResult: "Turno 1 completo, luego Turno 2…",
      finalFormula: `=ENCOL(${RANGO},1,VERDADERO)`,
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Quiénes hacen guardia",
      description: `En <strong>${ANCHORS[2]}</strong>: la lista de personas que aparecen en el rol, <strong>sin repetir</strong> y en orden alfabético.`,
      solution: [`Selecciona ${F(ANCHORS[2])}.`, `Escribe ${F(`=ORDENAR(UNICOS(ENCOL(${RANGO},1)))`)}.`],
      expectedResult: "Andrés, Carlos, Isabel, Lucía, Miguel, Valeria",
      finalFormula: `=ORDENAR(UNICOS(ENCOL(${RANGO},1)))`,
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Invitados en 3 columnas",
      description: `En <strong>${ANCHORS[3]}</strong>: acomoda la lista <strong>Invitados</strong> en filas de <strong>3 nombres</strong>. Los espacios que sobren al final deben quedar <strong>vacíos</strong> (no #N/D).`,
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        `AJUSTARFILAS(lista, columnas, relleno). Sin el relleno, los huecos salen como #N/D; con ${F('""')} quedan vacíos.`,
        `Escribe ${F('=AJUSTARFILAS(Invitados[Nombre],3,"")')}.`,
      ],
      expectedResult: "5 filas × 3 columnas",
      finalFormula: '=AJUSTARFILAS(Invitados[Nombre],3,"")',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Columnas de 5 nombres",
      description: `En <strong>${ANCHORS[4]}</strong>: ahora acomoda los invitados en <strong>columnas de 5</strong> nombres (hacia abajo y luego a la siguiente columna), con relleno vacío.`,
      solution: [`Selecciona ${F(ANCHORS[4])}.`, `Escribe ${F('=AJUSTARCOLS(Invitados[Nombre],5,"")')}.`],
      expectedResult: "5 filas × 3 columnas, llenado por columnas",
      finalFormula: '=AJUSTARCOLS(Invitados[Nombre],5,"")',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Guardias por persona",
      description: conditions(`En <strong>${ANCHORS[5]}</strong>: una tabla con cada persona y <strong>cuántas guardias</strong> tiene en la semana:`, [
        "<strong>p</strong> = la lista de personas de la Tarea 3 (guárdala con LET)",
        "<strong>CONTAR.SI</strong>(rango de turnos, p) cuenta las guardias de cada persona a la vez",
        "<strong>APILARH</strong>(p, conteo) las pone lado a lado",
      ]),
      solution: [`Selecciona ${F(ANCHORS[5])}.`, `Escribe ${F(LET_FORMULA)}.`],
      expectedResult: "Persona | número de guardias",
      finalFormula: LET_FORMULA,
    },
  ],
};
